import {Injectable} from '@angular/core';
import {ReplaySubject, Subject} from 'rxjs';
import {
	AgentDashBoardModel,
	AggregatorDashBoardModel,
	OutletManagerDashBoardModel
} from '../../shared/models/dashboard.model';
import {ApiClientHelper} from '../helpers/api-client.helper';
import {AppConfig} from '../../shared/config/app-config';
import {UserAlias} from '../../shared/constants';
import {StringHelper} from '../helpers/strings.helper';
import {MobileWebViewService} from './mobile-web-view.service';
import {BrowserHelper} from '../helpers/browser.helper';
import {LocalStorageService} from './local-storage.service';
import {Router} from '@angular/router';
import { DataLayerService } from './data-layer-service.service';
import { AuthenticationService } from './authentication.service';
import { MixPanelService } from './mix-panel.service';
// import * as zendesk from '../../../assets/scripts/zendesk.js';
declare const logoutFromZenDesk: any;

@Injectable({
	providedIn: 'root',
})
export class GeneralService {
	dashboardData: any = new ReplaySubject(1);
	dashboardDataError: any = new ReplaySubject(1);
	private closeNotification = new Subject<any>();
	closeNotificationSubscription$ = this.closeNotification.asObservable();

	private hideAppHeader = new Subject<any>();
	hideAppHeader$ = this.hideAppHeader.asObservable();
	private supportObservable = new Subject<any>();
	supportObservable$ = this.supportObservable.asObservable();
	private needhelpObservable = new Subject<any>();
	needhelpObservable$ = this.needhelpObservable.asObservable();
	// notification counter observable
	private notificationCounter = new Subject<any>();
	notificationCounter$ = this.notificationCounter.asObservable();

	constructor(
		private apiClientHelper: ApiClientHelper,
		public config: AppConfig,
		private localStorage: LocalStorageService,
		private authenticationService: AuthenticationService,
		private mobileWebViewService: MobileWebViewService,
		private router: Router,
		public dataLayerService: DataLayerService,
		private browserHelper: BrowserHelper,
		private mixpanelService: MixPanelService,
	) {}

	setCloseNotification() {
		this.closeNotification.next(false);
	}

	setHideAppHeader(hide: boolean) {
		this.hideAppHeader.next(hide);
	}

	loadDashboardData(user: string): void {
		switch (user) {
			case UserAlias.agent:
				this.loadAgentDashboardData();
				break;
			case UserAlias.agentManager:
				this.loadOutletDashboardData();
				break;
		}
	}

	loadAgentDashboardData() {
		this.getAgentDashboardData()
			.then((data) => {
				this.dashboardData.next(data);
			})
			.catch((error) => {
				const message = StringHelper.getErrorMessageHttpErrorResponse(error);
				this.dashboardDataError.next(message);
			});
	}

	loadOutletDashboardData() {
		this.getOutletManagerDashboardData()
			.then((data) => {
				this.dashboardData.next(data);
			})
			.catch((error) => {
				const message = StringHelper.getErrorMessageHttpErrorResponse(error);
				this.dashboardDataError.next(message);
			});
	}

	loadAggregatorDashboardData() {
		this.getAggregatorDashboardData()
			.then((data) => {
				this.dashboardData.next(data);
			})
			.catch((error) => {
				const message = StringHelper.getErrorMessageHttpErrorResponse(error);
				this.dashboardDataError.next(message);
			});
	}

	getAgentDashboardData(): Promise<AgentDashBoardModel> {
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v1/dashboard/agents?trend=3`;
		return this.apiClientHelper.getForPromise(url);
	}

	getOutletManagerDashboardData(): Promise<OutletManagerDashBoardModel> {
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v1/dashboard/agent-managers?trend=3`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAggregatorDashboardData(): Promise<AggregatorDashBoardModel> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/dashboard/aggregators?trend=3`;
		return this.apiClientHelper.getForPromise(url);
	}

	acceptTermsAndConditions(body: { accepted: boolean }): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/terms-and-conditions`;
		return this.apiClientHelper.postForPromise(url, body);
	}

	getTermsAndConditionsForBusinessOwner(user): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/terms-and-conditions/${user}`;
		return this.apiClientHelper.getForPromise(url);
	}

	reloadDashboardData(user) {
		this.loadDashboardData(user);
	}

	uploadFile(file) {
		const formData = new FormData();
		formData.append('file', file);
		const url = `${this.config.FILE_SERVICE}/upload`;
		return this.apiClientHelper.postForPromiseMultipart(url, formData);
	}

	downloadCertificate() {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/agents/download-certificate`;
		return this.apiClientHelper.getAsBlobForPromise(url);
	}

	logout(expiredToken = false) {
		this.handleLogout(expiredToken);
		this.router.navigateByUrl(`/login`);
	}

	handleLogout(expiredToken = false){
		const userRole = this.localStorage.getUserRole();
		if ([UserAlias.aggregator, UserAlias.clusterManager].includes(userRole)) {
			this.authenticationService.logout();
		}
		this.logoutZenDesk();
		this.localStorage.deleteAll();
		sessionStorage.clear();
		if (expiredToken) {
			sessionStorage.setItem('expired_token', JSON.stringify(expiredToken));
		}
		this.dataLayerService.resetUser();
		this.mixpanelService.reset();
	}

	logoutZenDesk() {
		try {
			logoutFromZenDesk();
		} catch (error) {
			console.log('Error login out Zendesk --> ', error);
		}
	}

	viewFileWithReference(reference: string) {
		const url = `${this.config.KUWEGO_IMAGE_URL}/view/${reference}`;
		return this.apiClientHelper.getAsBlobForPromise(url);
	}

	getFileDto(reference): Promise<any> {
		const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/file-dto/${reference}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getEndOfYearSummaryReport(businesId: string) {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/agents/${businesId}/end-of-year-summary`;
		return this.apiClientHelper.getForPromise(url);
	}

	uploadFileV2(file, contentTpe) {
		const formData = new FormData();
		formData.append('file', file);
		const url = `${this.config.KUWEGO_IMAGE_URL}/upload?contentType=${contentTpe}`;
		return this.apiClientHelper.postForPromiseMultipart(url, formData);
	}

	callSupportObervable(params) {
		this.supportObservable.next(params);
	}

	callNeedHelpObervable(params) {
		this.needhelpObservable.next(params);
	}

	checkUnreadNotification(params) {
		// counter
		this.notificationCounter.next(params);
	}
}
